import { Button, ButtonProps, Icon, Intent, Position, Spinner } from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import { Reaction } from '@retro-ui/api-contract';
import cx from 'classnames';

import { apiQueryClient } from '@f/api-client';
import { useSciFinderReference } from '@f/hooks';

import cls from './ReferenceReactionsButton.module.css';

function getColorCodeIntent(count: number | undefined) {
  if (count === undefined) {
    return Intent.PRIMARY;
  }
  if (count === 0) {
    return Intent.DANGER;
  }
  if (count >= 1 && count <= 9) {
    return Intent.WARNING;
  }
  return Intent.SUCCESS;
}

function getVisibleCount(count: number | undefined) {
  if (count === undefined) {
    return '';
  }
  if (count >= 100 * 1000) {
    return '99k+';
  }
  if (count >= 1000) {
    return `${Math.floor(count / 1000)}k`;
  }
  return count;
}

interface ReferenceReactionsButtonProps extends ButtonProps {
  reaction: Reaction;
}
export const ReferenceReactionsButton = ({ reaction, ...buttonProps }: ReferenceReactionsButtonProps) => {
  const { isLoading: isOpeningReferences, reactionToOpen, openReferences } = useSciFinderReference();

  const onOpenReferences = () => {
    void openReferences(reaction);
  };

  // @ts-ignore
  const reactionStatsQuery = apiQueryClient.referenceReactions.fetchReferenceReactionsStats.useQuery(
    ['referenceReactionsCount', reaction],
    {
      body: reaction,
    },
    {
      queryKey: ['referenceReactionsCount', reaction],
      staleTime: Infinity,
    }
  );

  const isOpening = reactionToOpen?.productSmiles === reaction.productSmiles && isOpeningReferences;
  const isLoadingStats = reactionStatsQuery.isLoading;
  const reactionStats = reactionStatsQuery.data?.body ?? {};

  const buttonIntent = isLoadingStats ? Intent.NONE : getColorCodeIntent(reactionStats.count);

  const visibleCount = getVisibleCount(reactionStats.count);

  return (
    <Tooltip2
      content={
        <>
          {reactionStats.hasExactMatch && (
            <p>
              <Icon icon="tick" /> Exactly matching reaction found!
            </p>
          )}
          {`Open ${reactionStats.count ?? ''} reference ${
            reactionStats.count === 1 ? 'reaction' : 'reactions'
          } in SciFinder.`}
          <br />
          <small>Please allow RetroScore to open popup windows if prompted.</small>
        </>
      }
      position={Position.TOP}
      hoverOpenDelay={500}
      className={cls.tooltip}
    >
      <>
        <Button
          {...buttonProps}
          loading={isOpening}
          onClick={onOpenReferences}
          intent={buttonIntent}
          className={cx(buttonProps.className, cls.button, { [cls.buttonExactMatch]: reactionStats.hasExactMatch })}
        >
          <div className={cls.buttonContent}>
            <Icon icon="manual" />
            {isLoadingStats ? <Spinner size={14} style={{ marginTop: '0.2rem' }} /> : visibleCount}
          </div>
        </Button>
        {reactionStats.hasExactMatch && <div className={cls.hasExactMatch} />}
      </>
    </Tooltip2>
  );
};
